import { defineStore } from 'pinia'
import { injToken } from '@shared/data/token'
// Keep direct import for synchronous cleanup in reset()
import { cancelBankBalanceStream } from './stream'
import { getMsgBroadcaster } from '@shared/wallet'
import { toHumanReadable } from '@injectivelabs/utils'
import { toUiCw20Balance } from '@shared/transformer/explorer'
import { getBankApi, getIndexerRestExplorerApi } from '@shared/service'
import {
  ExecArgCW20Send,
  MsgExecuteContractCompat
} from '@injectivelabs/sdk-ts/core/modules'
import { lazyPiniaAction } from '@/utils/pinia'
import {
  INJ_REQUIRED_FOR_GAS,
  CW20_WRAPPER_CONTRACT
} from '@/app/utils/constants'
import type { Coin } from '@injectivelabs/ts-types'
import type { TokenStatic } from '@injectivelabs/sdk-ts'

type BankStoreState = {
  bankBalances: Coin[]
  cw20Balances: Cw20CoinWithToken[]
}

type Cw20CoinWithToken = {
  denom: string
  amount: string
  token: undefined | TokenStatic
}

const initialStateFactory = (): BankStoreState => ({
  bankBalances: [],
  cw20Balances: []
})

export const useBankStore = defineStore('bank', {
  state: (): BankStoreState => initialStateFactory(),
  getters: {
    balanceMap: (state: BankStoreState) => {
      if (state.bankBalances.length === 0) {
        return {}
      }

      return state.bankBalances.reduce(
        (list, balance) => ({ ...list, [balance.denom]: balance.amount }),
        {} as Record<string, string>
      )
    },

    cw20BalanceMap: (state: BankStoreState) => {
      if (state.cw20Balances.length === 0) {
        return {}
      }

      return state.cw20Balances.reduce(
        (list, balance) => ({ ...list, [balance.denom]: balance.amount }),
        {} as Record<string, string>
      )
    },

    hasEnoughInjForGas: (state: BankStoreState) => {
      const injBalance = state.bankBalances.find(
        ({ denom }) => denom === injToken.denom
      )

      if (!injBalance) {
        return false
      }

      return toHumanReadable(injBalance.amount).gte(INJ_REQUIRED_FOR_GAS)
    }
  },
  actions: {
    streamBankBalance: lazyPiniaAction(
      () => import('./stream'),
      'streamBankBalance'
    ),

    transfer: lazyPiniaAction(() => import('./message'), 'transfer'),

    async redeemCw20ToFactory({
      amount,
      denom,
      cw20Address
    }: {
      denom: string
      amount: string
      cw20Address: string
    }) {
      const bankStore = useBankStore()
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      await walletStore.validateAndQueue()

      const message = MsgExecuteContractCompat.fromJSON({
        contractAddress: cw20Address,
        sender: sharedWalletStore.injectiveAddress,
        execArgs: ExecArgCW20Send.fromJSON({
          amount,
          contractAddress: CW20_WRAPPER_CONTRACT,
          msg: { unwrap_token: { denom } }
        })
      })

      const msgBroadcaster = await getMsgBroadcaster()
      await msgBroadcaster.broadcastV2({
        msgs: message,
        injectiveAddress: sharedWalletStore.injectiveAddress
      })

      await sharedBackupPromiseCall(() => bankStore.fetchBankBalance())
      await sharedBackupPromiseCall(() => bankStore.fetchCW20Balances())
    },

    async fetchBankBalance() {
      const bankStore = useBankStore()
      const sharedWalletStore = useSharedWalletStore()
      const bankApi = await getBankApi()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      const { balances } = await bankApi.fetchBalances(
        sharedWalletStore.injectiveAddress,
        { limit: 300 }
      )

      bankStore.$patch({
        bankBalances: balances || []
      })
    },

    async fetchIfNotFetchedBankBalance() {
      const bankStore = useBankStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      if (bankStore.bankBalances.length > 0) {
        return
      }

      await bankStore.fetchBankBalance()
    },

    async fetchCW20Balances() {
      const bankStore = useBankStore()
      const sharedWalletStore = useSharedWalletStore()
      const indexerRestExplorerApi = await getIndexerRestExplorerApi()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      const cw20Balances = (
        await indexerRestExplorerApi.fetchCW20BalancesNoThrow(
          sharedWalletStore.injectiveAddress
        )
      )
        .map(toUiCw20Balance)
        .map((balance) => ({
          ...balance,
          amount: balance.balance,
          token: balance.token as TokenStatic
        }))
        .filter((balance) => balance)

      bankStore.$patch({
        cw20Balances
      })
    },

    async fetchIfNotFetchedCW20Balances() {
      const bankStore = useBankStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      if (bankStore.cw20Balances.length > 0) {
        return
      }

      await bankStore.fetchCW20Balances()
    },

    async fetchCW20BalancesAndTokens() {
      const bankStore = useBankStore()
      const tokenStore = useTokenStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.injectiveAddress) {
        return
      }

      await bankStore.fetchIfNotFetchedCW20Balances()
      await tokenStore.fetchCw20Tokens()
    },

    reset() {
      const bankStore = useBankStore()

      cancelBankBalanceStream()

      bankStore.$reset()
    }
  }
})
